import React, { useCallback } from "react";
import {
  BackButton,
  FooterV2,
  OfferingHeader as OfferingHeaderV2,
} from "../components";
import { PartnerOffering } from "../../data/partner_offering";
import { Helmet } from "react-helmet";
import { Spacer } from "../../offering/spacer";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { useMediaQuery, useQueryString } from "../../hooks";
import { useAnalytics } from "../../lib/analytics/analytics";
import { PartnerConfig } from "../../offering/partner_config";
import { TalkWithUsPrompt } from "../components/talk_with_us_prompt";
import styles from "./template_offering_listing_base.module.css";
import {
  useDisplayMerchantNameAndAddress,
  useGalleryEmptyStateUpsell,
  useMeetingRoomGalleryImprovement,
} from "../../core/splitio";
import { Hidden } from "../../offering/hidden";
import Container from "react-bootstrap/esm/Container";
import { CatalogPartnerInfo } from "@/offering/catalog_partner_info";
import { useHandlePressBack } from "../hooks/use_handle_press_back";
import { OfferingHeader } from "@/offering/offering_header";
import { offeringConfig } from "@/offering/offering_config";
import { useUTMParams } from "@/offering/utm";

export interface OfferingParams {
  fromCatalog?: string;
  fromCategory?: string;
}

interface TemplateOfferingListingLayoutProps {
  title: string;
  listingContent: React.ReactNode;
  mobileFilters?: React.ReactNode;
  desktopFilters?: React.ReactNode;
  partnerOffering: PartnerOffering;
  partnerConfig: PartnerConfig;
  onLocaleSwitch?: () => void;
  headerBar?: React.ReactNode;
  upsellSpaces?: JSX.Element;
  hideBackButton?: boolean;
  partnerName?: string;
  locationAdress?: string;
}

export function TemplateOfferingListingLayout(
  props: TemplateOfferingListingLayoutProps
) {
  const {
    title,
    listingContent,
    mobileFilters,
    desktopFilters,
    partnerOffering,
    partnerConfig,
    onLocaleSwitch,
    headerBar,
    upsellSpaces,
    hideBackButton,
    partnerName,
    locationAdress,
  } = props;

  const queryParams = useQueryString<OfferingParams>();

  const meetingRoomGalleryImprovementFlag = useMeetingRoomGalleryImprovement(
    partnerOffering.id
  );
  const galleryEmptyStateUpsellFlag = useGalleryEmptyStateUpsell(
    partnerOffering.id
  );

  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const isMobile = device === "mobile";
  const {
    hubspotForm,
    talkWithUsMessage,
    showTalkWithUsPrompt = true,
    localeOptions,
    favicon,
    hubspotForms,
    instagramLink,
  } = partnerConfig;

  const { handlePressBackFromDetailsPage } = useHandlePressBack();

  const handlePressBack = useCallback(() => {
    handlePressBackFromDetailsPage(
      queryParams.fromCategory
        ? `category/${queryParams.fromCategory}`
        : `catalog/${partnerOffering.partner.partnerSlug}`
    );
  }, [
    handlePressBackFromDetailsPage,
    partnerOffering.partner.partnerSlug,
    queryParams.fromCategory,
  ]);

  const analytics = useAnalytics();
  const { utmCampaign } = useUTMParams();
  const displayMerchantNameAndAddress = useDisplayMerchantNameAndAddress();

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      {partnerConfig.slug === offeringConfig.WeWork.partnerSlug ? (
        <OfferingHeader
          logo={partnerOffering.partner.logoUrl}
          homepageURL={partnerOffering.partner.homepageUrl}
          hubspotForm={hubspotForm}
          offeringID={partnerOffering.id}
          partnerID={partnerOffering.partner.id}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          localeOptions={localeOptions}
          onLocaleSwitch={onLocaleSwitch}
          showLanguageSelector={!!partnerConfig.showLanguageSelector}
        />
      ) : (
        <OfferingHeaderV2
          logo={partnerOffering.partner.logoUrl}
          homepageURL={partnerOffering.partner.homepageUrl}
          hubspotForm={hubspotForm}
          hubspotForms={hubspotForms}
          offeringID={partnerOffering.id}
          partnerID={partnerOffering.partner.id}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          talkWithUsMessage={talkWithUsMessage}
          localeOptions={localeOptions}
          onLocaleSwitch={onLocaleSwitch}
        />
      )}
      {headerBar}
      <div className={styles.bodyContainer}>
        {!isMobile && !hideBackButton && (
          <Container>
            <Spacer size={22} />
            <BackButton
              label={fbt("backToAllOffering")}
              onPress={() => {
                handlePressBack();

                analytics.event(
                  "Breadcrumb Click",
                  {
                    Source: "WL",
                  },
                  { onlyProviders: ["mixpanel", "customerio"] }
                );
              }}
            />
            <Spacer size={22} />
          </Container>
        )}

        <Spacer size={16} />

        <Container>
          {displayMerchantNameAndAddress &&
            utmCampaign === "g-book-now" &&
            partnerName &&
            locationAdress && (
              <CatalogPartnerInfo
                partnerName={partnerName}
                locationAdress={locationAdress}
              />
            )}
          <h3 className={styles.title}>{title}</h3>
          {partnerOffering.description && (
            <div className={styles.description}>
              <Spacer size={16} />
              <div
                dangerouslySetInnerHTML={{
                  __html: partnerOffering.description,
                }}
              />
            </div>
          )}
        </Container>

        {(desktopFilters || mobileFilters) && (
          <Spacer size={isMobile ? 32 : 40} />
        )}
        <div
          className={
            partnerConfig.slug === offeringConfig.WeWork.partnerSlug
              ? styles.topSticky
              : styles.headerSticky
          }
        >
          {isMobile && meetingRoomGalleryImprovementFlag && mobileFilters}
          {!isMobile &&
            (meetingRoomGalleryImprovementFlag ? (
              desktopFilters
            ) : (
              <Container>{desktopFilters}</Container>
            ))}
        </div>
        <Spacer size={isMobile ? 32 : 40} />

        <div>{listingContent}</div>

        {galleryEmptyStateUpsellFlag && upsellSpaces && (
          <Container>{upsellSpaces}</Container>
        )}

        {/* TALK TO US MOBILE */}
        <Spacer size={140} />
        <div className={styles.footerWrapper}>
          <div className="py-1 d-flex justify-content-center fw-bold d-block d-md-none">
            {showTalkWithUsPrompt ? (
              <TalkWithUsPrompt
                partnerID={partnerOffering.partner.id}
                hubspotForm={hubspotForm}
                messageableID={partnerOffering.id}
                hubspotForms={hubspotForms}
              />
            ) : (
              talkWithUsMessage && `${talkWithUsMessage}`
            )}
          </div>
        </div>
      </div>
      <Spacer size={30} />
      <FooterV2 instagramLink={instagramLink} />

      {!meetingRoomGalleryImprovementFlag && (
        <Hidden md>
          <div style={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
            {mobileFilters}
          </div>
        </Hidden>
      )}
    </div>
  );
}
